import React, { useState, useRef, useEffect } from 'react'
import styled from '@emotion/styled'
import { useTranslation } from 'next-i18next'
import { UiIcon } from '../../atoms/Icon'
import { DEFAULT_THEME_COLORS } from 'css/theme'
import { TYPOGRAPHY_PRESETS } from 'styles/mixins/presets'
import { Input } from 'modules/ui/atoms/Input'
import TextPlaceloader from 'modules/ui/atoms/Loaders/TextPlaceloader'
import { KeyboardArrowDown } from '@classy/campaign-page-blocks'
import useComboboxKeyHandlers from 'modules/order/components/designation/use-combobox-key-handlers'

export interface ComboboxOption {
  value: string | number
  label: string
  isHeader?: boolean
}

export interface ComboboxProps {
  // Display props
  fieldLabel: string
  placeholder: string
  defaultText?: string
  selectedValue?: string | number
  selectedLabel?: string

  // Data props
  options: ComboboxOption[]
  loading: boolean

  // Behavior props
  onSelect: (value: string | number, label: string) => void
  onSearchChange?: (query: string) => void
  searchQuery?: string
  onDropdownToggle?: (isOpen: boolean) => void
  clearable?: boolean
  onClear?: () => void

  // Accessibility props
  ariaLabel: string
  ariaOptionsLabel: string
  dataIdPrefix: string

  // Optional props
  showGroupHeaders?: boolean
  noResultsText?: string
  searchPlaceholder?: string
}

const FieldContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`

const FieldLabel = styled.span`
  ${TYPOGRAPHY_PRESETS.classyParagraphMd}
  color: ${DEFAULT_THEME_COLORS['--theme-color--neutral-10']};
  white-space: nowrap;
`

const SelectorContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
`

const ClickableSelector = styled.div<{ isOpen: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 0;
  border: none;
  background: transparent;
  cursor: pointer;

  &:focus {
    outline: 2px solid ${DEFAULT_THEME_COLORS['--theme-color__brand-primary']};
    outline-offset: 2px;
  }

  &:hover .selector-text {
    border-bottom-color: ${DEFAULT_THEME_COLORS['--theme-color__brand-primary']};
  }
`

const SelectorText = styled.span<{ isDefault: boolean }>`
  ${TYPOGRAPHY_PRESETS.classyParagraphMd}
  color: ${(props) =>
    props.isDefault
      ? DEFAULT_THEME_COLORS['--theme-color--neutral-45']
      : 'var(--color-border-input-base, #6F6F6F)'};
  font-weight: normal;
  border-bottom: 1px dotted ${DEFAULT_THEME_COLORS['--theme-color--neutral-45']};
  text-align: center;
  font-feature-settings: 'liga' off, 'clig' off;
  transition: border-bottom-color 0.2s ease;
`

const StyledArrowDown = styled(KeyboardArrowDown)<{ isOpen: boolean }>`
  width: 20px;
  height: 24px;
  transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease;
  color: ${DEFAULT_THEME_COLORS['--theme-color--neutral-45']};

  path {
    fill: ${DEFAULT_THEME_COLORS['--theme-color--neutral-45']};
  }
`

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: ${DEFAULT_THEME_COLORS['--theme-color--neutral-90']};
  cursor: pointer;
  padding: 0;
  margin-right: 4px;
  border-radius: 50%;
  color: ${DEFAULT_THEME_COLORS['--theme-color--neutral-10']};

  &:focus {
    outline: 2px solid ${DEFAULT_THEME_COLORS['--theme-color__brand-primary']};
    outline-offset: 1px;
  }

  svg {
    width: 12px;
    height: 12px;
  }
`

const SelectorControls = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`

const DropdownContainer = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid ${DEFAULT_THEME_COLORS['--theme-color--neutral-80']};
  border-radius: 4px;
  max-height: 300px;
  overflow: hidden;
  z-index: 1000;
  display: ${(props) => (props.isOpen ? 'block' : 'none')};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`

const SearchContainer = styled.div`
  padding: 8px;
  border-bottom: 1px solid ${DEFAULT_THEME_COLORS['--theme-color--neutral-90']};
`

const OptionsContainer = styled.div`
  max-height: 200px;
  overflow-y: auto;
`

const OptionItem = styled.div<{
  isHeader?: boolean
  isSelected?: boolean
  isHighlighted?: boolean
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  cursor: ${(props) => (props.isHeader ? 'default' : 'pointer')};
  background: ${(props) => {
    if (props.isHighlighted) return DEFAULT_THEME_COLORS['--theme-color--neutral-90']
    if (props.isSelected) return DEFAULT_THEME_COLORS['--theme-color--neutral-95']
    return 'transparent'
  }};
  font-weight: ${(props) => (props.isHeader ? 'bold' : 'normal')};
  font-size: 14px;
  color: ${DEFAULT_THEME_COLORS['--theme-color--neutral-10']};

  &:hover {
    background: ${(props) =>
      !props.isHeader ? DEFAULT_THEME_COLORS['--theme-color--neutral-95'] : 'transparent'};
  }

  span {
    flex: 1;
    text-align: left;
  }
`

const LoadingContainer = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const NoResultsContainer = styled.div`
  padding: 16px;
  text-align: center;
  color: ${DEFAULT_THEME_COLORS['--theme-color--neutral-45']};
  font-size: 14px;
`

export default function Combobox({
  fieldLabel,
  placeholder,
  defaultText,
  selectedValue,
  selectedLabel,
  options,
  loading,
  onSelect,
  onSearchChange,
  searchQuery = '',
  onDropdownToggle,
  clearable = false,
  onClear,
  ariaLabel,
  ariaOptionsLabel,
  dataIdPrefix,
  showGroupHeaders = true,
  noResultsText,
  searchPlaceholder,
}: ComboboxProps) {
  const { t } = useTranslation()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [activeDescendant, setActiveDescendant] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const listboxRef = useRef<HTMLDivElement>(null)

  // Check if we're showing default text
  const isDefaultText = !selectedLabel || selectedLabel === ''
  const displayText = isDefaultText ? defaultText || placeholder : selectedLabel

  // Determine if clear button should be shown
  const shouldShowClearButton = clearable && !isDefaultText && selectedValue != null

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)

        if (onSearchChange) {
          onSearchChange('')
        }
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen, onSearchChange])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen) {
      setTimeout(() => {
        const searchInput = document.querySelector(
          `[data-id="${dataIdPrefix}-search"]`,
        ) as HTMLInputElement

        searchInput?.focus()
      }, 100)
    }
  }, [isDropdownOpen, dataIdPrefix])

  // Scroll highlighted option into view for accessibility
  useEffect(() => {
    if (activeDescendant && isDropdownOpen) {
      const element = document.getElementById(activeDescendant)

      if (element) {
        element.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [activeDescendant, isDropdownOpen])

  const handleSelectorClick = () => {
    const newDropdownState = !isDropdownOpen

    if (!isDropdownOpen) {
      if (onSearchChange) {
        onSearchChange('')
      }

      setHighlightedIndex(-1)
      setActiveDescendant('')
    }

    setIsDropdownOpen(newDropdownState)

    if (onDropdownToggle) {
      onDropdownToggle(newDropdownState)
    }
  }

  const handleOptionSelect = (value: string | number, label: string) => {
    onSelect(value, label)
    setIsDropdownOpen(false)

    if (onSearchChange) {
      onSearchChange('')
    }

    if (onDropdownToggle) {
      onDropdownToggle(false)
    }
  }

  const handleSearchChange = (value: string) => {
    if (onSearchChange) {
      onSearchChange(value)
    }

    setHighlightedIndex(-1)
    setActiveDescendant('')
  }

  const handleSearchBlur = () => {
    setHighlightedIndex(-1)
    setActiveDescendant('')
  }

  const closeDropdown = () => {
    setIsDropdownOpen(false)

    if (onSearchChange) {
      onSearchChange('')
    }

    setHighlightedIndex(-1)
    setActiveDescendant('')

    if (onDropdownToggle) {
      onDropdownToggle(false)
    }
  }

  const selectOption = (value: string | number, label: string) => {
    handleOptionSelect(value, label)
    closeDropdown()
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent dropdown toggle

    if (onClear) {
      onClear()
    }
  }

  const handleClearKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.stopPropagation() // Prevent dropdown toggle

      if (onClear) {
        onClear()
      }
    }
  }

  const { handleTriggerKeyDown, handleSearchKeyDown, handleListboxKeyDown } =
    useComboboxKeyHandlers<ComboboxOption>({
      items: options,
      isOpen: isDropdownOpen,
      loading,
      highlightedIndex,
      setHighlightedIndex,
      setActiveDescendant,
      onSelect: (item: ComboboxOption) => {
        handleOptionSelect(item.value, item.label)
      },
      onToggle: handleSelectorClick,
      onClose: closeDropdown,
      idPrefix: `${dataIdPrefix}-option`,
      filterSelectableItems: (item) => !item.isHeader,
      listboxRef,
    })

  return (
    <FieldContainer ref={dropdownRef}>
      <FieldLabel>{fieldLabel}</FieldLabel>

      <SelectorContainer>
        <ClickableSelector
          isOpen={isDropdownOpen}
          onClick={handleSelectorClick}
          onKeyDown={handleTriggerKeyDown}
          tabIndex={0}
          role="combobox"
          aria-expanded={isDropdownOpen}
          aria-haspopup="listbox"
          aria-controls={`${dataIdPrefix}-listbox`}
          aria-activedescendant={activeDescendant}
          aria-autocomplete="list"
          aria-label={ariaLabel}
          data-id={`${dataIdPrefix}-selector`}
        >
          <SelectorText className="selector-text" isDefault={isDefaultText}>
            {displayText}
          </SelectorText>
          <SelectorControls>
            {shouldShowClearButton && (
              <ClearButton
                onClick={handleClear}
                onKeyDown={handleClearKeyDown}
                type="button"
                aria-label="Clear selection"
                data-id={`${dataIdPrefix}-clear`}
                tabIndex={0}
              >
                <UiIcon name="close" w={12} h={12} />
              </ClearButton>
            )}
            <StyledArrowDown isOpen={isDropdownOpen} />
          </SelectorControls>
        </ClickableSelector>
      </SelectorContainer>

      <DropdownContainer isOpen={isDropdownOpen}>
        {onSearchChange && (
          <SearchContainer>
            <Input
              placeholder={searchPlaceholder || `Search ${fieldLabel.toLowerCase()}`}
              value={searchQuery}
              onChange={handleSearchChange}
              onBlur={handleSearchBlur}
              onKeyDown={handleSearchKeyDown}
              leftIcon="search"
              dataId={`${dataIdPrefix}-search`}
            />
          </SearchContainer>
        )}

        <OptionsContainer
          ref={listboxRef}
          id={`${dataIdPrefix}-listbox`}
          role="listbox"
          tabIndex={-1}
          onKeyDown={handleListboxKeyDown}
          aria-label={ariaOptionsLabel}
        >
          {loading && (
            <LoadingContainer>
              <TextPlaceloader />
              <TextPlaceloader />
              <TextPlaceloader />
            </LoadingContainer>
          )}

          {!loading && options.length === 0 && searchQuery && (
            <NoResultsContainer>
              {noResultsText ||
                t('Combobox.noResults', 'No results found for "{{query}}"', {
                  query: searchQuery,
                })}
            </NoResultsContainer>
          )}

          {!loading && options.length > 0 && (
            <>
              {options.map((item, index) => {
                // Handle header items
                if (item.isHeader) {
                  if (!showGroupHeaders) {
                    return null
                  }

                  return (
                    <OptionItem
                      key={`header-${item.label}-${index}`}
                      isHeader={true}
                      isSelected={false}
                      isHighlighted={false}
                      role="presentation"
                      aria-hidden="true"
                    >
                      <span>{item.label}</span>
                    </OptionItem>
                  )
                }

                const isSelected = selectedValue === item.value

                const validOptions = options.filter((option) => !option.isHeader)

                const optionIndex = validOptions.findIndex((option) => option.value === item.value)

                const isHighlighted = optionIndex === highlightedIndex

                return (
                  <OptionItem
                    key={`option-${item.value}`}
                    isSelected={isSelected}
                    isHighlighted={isHighlighted}
                    onClick={() => selectOption(item.value, item.label)}
                    role="option"
                    aria-selected={isSelected}
                    id={`${dataIdPrefix}-option-${item.value}`}
                    data-id={`${dataIdPrefix}-option-${item.value}`}
                  >
                    <span>{item.label}</span>
                    {isSelected && (
                      <UiIcon
                        name="big_check"
                        w={16}
                        h={16}
                        color={DEFAULT_THEME_COLORS['--theme-color__brand-primary']}
                      />
                    )}
                  </OptionItem>
                )
              })}
            </>
          )}
        </OptionsContainer>
      </DropdownContainer>
    </FieldContainer>
  )
}
